<template>
    <modals-side-generic
    sizeClass="big"
    invertTitleSubtitle
    :headerColor="operator.condition ? 'var(--warning)' : colorHandler(operator.status?.operatorStatusValue)"
    :title="operator.name"
    :subTitle="operator.status?.operatorStatusDescription"
    :icon="operator.condition ? 'clock' : headerIconHandler(operator.status?.operatorStatusValue)"
    :showFooter="!showSpinner && !applyErrorState && showFooter"
    :buttonsFooterData="buttonsFooterData.filter(ele => !ele.hidden)"
    :noPaddingBottom="operatorDetails && !operatorDetails.condition"
    @hideMe="[emit('hideMe'), cancelAllPendingRequests()]">
        <div class="operator-details group-wrapper" v-if="!showSpinner && !applyErrorState">
            <!-- Status Block -->
            <detail-status-block
            :operatorDetails="operatorDetails"/>

            <!-- Editions Block -->
            <detail-editions-block
            v-if="operatorDetails.condition"
            :operatorDetails="operatorDetails"
            :accounts="getAccounts"
            :operationTypesList="getOperationsTypesList"/>

            <!-- Data Block -->
            <detail-data-block
            :operatorDetails="operatorDetails"
            @resetPassword="resetPasswordHandler"/>

            <!-- Operator Accounts Divider -->
            <divider-generic
            applyBorder
            icon="lock"
            :title="t('approvals.operators.operatorDetails.accountsTitle')"/>
            <account-accessed-block
            class="operation-info"
            v-for="account in operatorDetails.accountAccessConfig"
            :key="account.account.id"
            :account="account"
            :description="operatorDetails.accounts.find( acc => acc.id === parseInt(account.account.id)).description"
            :defaultAccount="operatorDetails.defaultAccountId"/>

        </div>

        <div class="remove-block" v-if="!showSpinner && !applyErrorState && !operatorDetails.condition">
            <button-negative
            icon="delete"
            iconPosition="right"
            :label="t('approvals.operators.operatorDetails.delete')"
            @clicked="navigateByName(routeList.APPROVALS_OPERATORS_DELETE_NAME, { operatorId: operator.id })"/>
        </div>

        <!-- Detail Error State -->
        <empty-state-error v-if="applyErrorState && !showSpinner" :message="t('approvals.operators.operatorDetails.movementDetailError')"/>

        <!-- Feedback Toast -->
        <toasts-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showFeedbackToast = false"/>

        <!-- Loader Generic -->
        <loaders-generic
        v-if="showSpinner || showOverlaySpinner"
        fullSize
        :isOverlay="showOverlaySpinner"/>
    </modals-side-generic>
</template>

<script setup>
import ModalsSideGeneric from '@/components/generic/modals/side/Generic'
import DetailStatusBlock from '@/components/features/approvals/operators/details/DetailStatusBlock'
import DetailDataBlock from '@/components/features/approvals/operators/details/DetailDataBlock'
import DetailEditionsBlock from '@/components/features/approvals/operators/details/DetailEditionsBlock'
import DividerGeneric from '@/components/generic/dividers/Generic'
import AccountAccessedBlock from '@/components/features/approvals/operators/details/AccountAccessedBlock'
import ButtonNegative from '@/components/generic/buttons/Negative'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
/* Vue & Helpers */
import { ref, computed } from 'vue'
import { headerIconHandler, colorHandler } from '@/assets/scripts/mappers/operators'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestOperatorDetail, requestOperationsTypes, requestAccounts, resetPassword } = mapActions(moduleMapper.APPROVALS_OPERATORS)
const { getAccounts, getOperationsTypesList } = mapGetters(moduleMapper.APPROVALS_OPERATORS)

/* Emmitters */
const emit = defineEmits(['hideMe'])

const props = defineProps({
    operator: Object
})

/* Data */
const operatorDetails = ref(null)
const showSpinner = ref(false)
const showOverlaySpinner = ref(false)

const showFooter = computed(() => {
    if (!operatorDetails.value.status) return false
    return (operatorDetails.value?.condition &&
    (operatorDetails.value.status.operatorStatusValue === 3 || operatorDetails.value.status.operatorStatusValue === 2)) || !operatorDetails.value.condition
})

const buttonsFooterData = computed(() => {
    return [{
        label: t('approvals.operators.operatorDetails.block'),
        icon: 'block',
        buttonPosition: 'left',
        hidden: operatorDetails.value && operatorDetails.value.status?.operatorStatusValue === 4,
        callbackFunction: () => { navigateByName(routeList.APPROVALS_OPERATORS_BLOCK_NAME, { operatorId: props.operator.id }) }
    }, {
        label: t('approvals.operators.operatorDetails.unblock'),
        icon: 'unlock',
        buttonPosition: 'left',
        hidden: operatorDetails.value && operatorDetails.value.status?.operatorStatusValue !== 4,
        callbackFunction: () => { navigateByName(routeList.APPROVALS_OPERATORS_UNBLOCK_NAME, { operatorId: props.operator.id }) }
    }, {
        label: t('approvals.operators.operatorDetails.edit'),
        icon: 'pencil',
        iconPosition: 'right',
        buttonType: 'primary',
        hidden: operatorDetails.value && operatorDetails.value.condition,
        callbackFunction: () => { navigateByName(routeList.APPROVALS_OPERATORS_EDIT_NAME, { operatorId: props.operator.id }) }
    }]
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestDetails = async () => {
	showSpinner.value = true

	try {
        operatorDetails.value = await requestOperatorDetail(props.operator.id)
        await requestOperationsTypes()
        await requestAccounts()
        showSpinner.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
		showSpinner.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, requestDetails)
	}
}

const resetPasswordHandler = async () => {
    showOverlaySpinner.value = true

    try {
        const res = await resetPassword(props.operator.id)
        showOverlaySpinner.value = false
        successHandler(res.data)
    } catch (error) {
        showOverlaySpinner.value = false
		errorHandler(error.response.data, resetPasswordHandler)
    }
}

/*************************************************
 *                                                *
 *             Success/Error Handlers             *
 *                                                *
 *************************************************/
const showFeedbackToast = ref(false)
const applyErrorState = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = data => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}
requestDetails()
</script>

<style lang="scss" scoped>
.operation-info:not(:last-of-type)  {
    margin-bottom: 16px;
}

.remove-block {
    width: 100%;
    height: 120px;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: rgba(var(--negative-rgb), 0.08);
}
</style>
